import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getDashboardApi } from "../api/dashboardApi";
import DashboardAttentionSection from "../components/dashboard/DashboardAttentionSection";
import DashboardCountCards from "../components/dashboard/DashboardCountCards";
import DashboardDailyTable from "../components/dashboard/DashboardDailyTable";
import DashboardExpenseMix from "../components/dashboard/DashboardExpenseMix";
import DashboardPeriodControls from "../components/dashboard/DashboardPeriodControls";
import DashboardStatusBreakdown from "../components/dashboard/DashboardStatusBreakdown";
import DashboardTeamTable from "../components/dashboard/DashboardTeamTable";
import DetailPageHeader from "../components/common/DetailPageHeader";
import FinancialSummaryCards from "../components/common/FinancialSummaryCards";
import { TestId } from "../constants/testIds";
import type { AppDispatch, RootState } from "../store";
import { fetchOperationsTeams } from "../store/features/operationsTeamSlice";
import type translations from "../translations/translations.json";
import type { DashboardData, DashboardPreset } from "../types/dashboard";
import { formatDashboardDelta } from "../utils/dashboardDelta";
import { getErrorMessage } from "../utils/errorHandling";

type TranslationKeys = keyof typeof translations.en;

const DEFAULT_PRESET: DashboardPreset = "this_month";

const isDashboardPreset = (value: string | null): value is DashboardPreset =>
  value === "today" ||
  value === "yesterday" ||
  value === "this_week" ||
  value === "last_week" ||
  value === "this_month" ||
  value === "last_month" ||
  value === "custom";

const parseCalendarDate = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const formatCalendarDate = (date: Date | null): string => {
  if (!date) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatPeriodSubtitle = (from: string, to: string, timezone?: string): string => {
  const fromDate = parseCalendarDate(from);
  const toDate = parseCalendarDate(to);
  if (!fromDate || !toDate) {
    return `${from} – ${to}`;
  }

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: timezone || undefined,
  };

  if (from === to) {
    return new Intl.DateTimeFormat(undefined, options).format(fromDate);
  }

  const sameMonth =
    fromDate.getFullYear() === toDate.getFullYear() && fromDate.getMonth() === toDate.getMonth();

  if (sameMonth) {
    const monthYear = new Intl.DateTimeFormat(undefined, {
      month: "short",
      year: "numeric",
      timeZone: timezone || undefined,
    }).format(fromDate);
    return `${fromDate.getDate()}–${toDate.getDate()} ${monthYear}`;
  }

  const fromLabel = new Intl.DateTimeFormat(undefined, options).format(fromDate);
  const toLabel = new Intl.DateTimeFormat(undefined, options).format(toDate);
  return `${fromLabel} – ${toLabel}`;
};

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { operationsTeams } = useSelector((state: RootState) => state.operationsTeams);
  const [searchParams, setSearchParams] = useSearchParams();

  const presetParam = searchParams.get("preset");
  const preset: DashboardPreset = isDashboardPreset(presetParam) ? presetParam : DEFAULT_PRESET;
  const fromParam = searchParams.get("from") ?? "";
  const toParam = searchParams.get("to") ?? "";
  const teamOperationsId = searchParams.get("teamOperationsId") ?? "";

  const [data, setData] = useState<DashboardData | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customFrom, setCustomFrom] = useState<Date | null>(() => parseCalendarDate(fromParam));
  const [customTo, setCustomTo] = useState<Date | null>(() => parseCalendarDate(toParam));
  const abortRef = useRef<AbortController | null>(null);
  const hasLoadedRef = useRef(false);

  const selectedTeam = useMemo(
    () => operationsTeams.find((team) => team._id === teamOperationsId) ?? null,
    [operationsTeams, teamOperationsId],
  );

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (!value) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const loadDashboard = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const isFirstLoad = !hasLoadedRef.current;
    if (isFirstLoad) {
      setError(null);
    } else {
      setRefreshing(true);
    }

    try {
      const response = await getDashboardApi(
        {
          preset,
          from: preset === "custom" ? fromParam || undefined : undefined,
          to: preset === "custom" ? toParam || undefined : undefined,
          teamOperationsId: teamOperationsId || undefined,
        },
        { signal: controller.signal },
      );

      if (!controller.signal.aborted) {
        setData(response);
        hasLoadedRef.current = true;
        setError(null);
      }
    } catch (err: unknown) {
      if (!controller.signal.aborted) {
        const message = getErrorMessage(err, t, "fetchDashboard");
        setError(message);
      }
    } finally {
      if (!controller.signal.aborted) {
        setRefreshing(false);
      }
    }
  }, [fromParam, preset, t, teamOperationsId, toParam]);

  useEffect(() => {
    dispatch(fetchOperationsTeams());
  }, [dispatch]);

  useEffect(() => {
    loadDashboard();
    return () => {
      abortRef.current?.abort();
    };
  }, [loadDashboard]);

  useEffect(() => {
    setCustomFrom(parseCalendarDate(fromParam));
    setCustomTo(parseCalendarDate(toParam));
  }, [fromParam, toParam]);

  const handlePresetChange = (nextPreset: DashboardPreset) => {
    if (nextPreset === "custom") {
      updateSearchParams({
        preset: nextPreset,
        from: fromParam || formatCalendarDate(customFrom),
        to: toParam || formatCalendarDate(customTo),
      });
      return;
    }

    updateSearchParams({
      preset: nextPreset,
      from: null,
      to: null,
    });
  };

  const handleTeamChange = (nextTeamOperationsId: string) => {
    updateSearchParams({
      teamOperationsId: nextTeamOperationsId || null,
    });
  };

  const handleApplyCustomRange = () => {
    updateSearchParams({
      preset: "custom",
      from: formatCalendarDate(customFrom) || null,
      to: formatCalendarDate(customTo) || null,
    });
  };

  if (data === null && !error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && data === null) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  if (!data) {
    return null;
  }

  const subtitle = formatPeriodSubtitle(data.period.from, data.period.to, data.period.timezone);
  const metaLine = `${data.period.timezone} · ${
    selectedTeam?.name ?? t("dashboard.allTeams" as TranslationKeys)
  }`;

  return (
    <Box sx={{ pb: 4 }} data-testid={TestId.DASHBOARD.PAGE}>
      <DetailPageHeader
        title={t("Dashboard" as TranslationKeys)}
        subtitle={subtitle}
        metaLine={metaLine}
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {refreshing && <CircularProgress size={20} />}
            <DashboardPeriodControls
              preset={preset}
              teamOperationsId={teamOperationsId}
              teams={operationsTeams}
              onPresetChange={handlePresetChange}
              onTeamChange={handleTeamChange}
            />
          </Box>
        }
      />

      {preset === "custom" && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            mb: 2,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
              <DatePicker
                label={t("dashboard.periodCustom" as TranslationKeys)}
                value={customFrom}
                onChange={(value) => setCustomFrom(value)}
                slotProps={{
                  textField: {
                    size: "small",
                    inputProps: { "data-testid": TestId.DASHBOARD.CUSTOM_FROM },
                  },
                }}
              />
              <DatePicker
                label={t("dashboard.periodCustom" as TranslationKeys)}
                value={customTo}
                onChange={(value) => setCustomTo(value)}
                slotProps={{
                  textField: {
                    size: "small",
                    inputProps: { "data-testid": TestId.DASHBOARD.CUSTOM_TO },
                  },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomRange}
                data-testid={TestId.DASHBOARD.APPLY_CUSTOM}
              >
                {t("dashboard.applyCustomRange" as TranslationKeys)}
              </Button>
            </Box>
          </LocalizationProvider>
        </Paper>
      )}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <DashboardCountCards
        orders={data.kpis.orders}
        standbyOrders={data.kpis.standbyOrders}
        reports={data.kpis.reports}
      />

      <FinancialSummaryCards
        orientation="horizontal"
        totalPaid={data.kpis.totalPaid.current}
        totalVerified={data.kpis.totalApprovedCost.current}
        difference={data.kpis.difference.current}
        totalPaidLabel={t("reports.totalPaid" as TranslationKeys)}
        totalVerifiedLabel={t("reports.totalApproved" as TranslationKeys)}
        differenceLabel={t("reports.difference" as TranslationKeys)}
        totalPaidTestId={TestId.DASHBOARD.TOTAL_PAID}
        totalVerifiedTestId={TestId.DASHBOARD.TOTAL_APPROVED}
        differenceTestId={TestId.DASHBOARD.DIFFERENCE}
        totalPaidFooter={
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {formatDashboardDelta(data.kpis.totalPaid.current, data.kpis.totalPaid.previous)}
          </Typography>
        }
        totalVerifiedFooter={
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {formatDashboardDelta(
              data.kpis.totalApprovedCost.current,
              data.kpis.totalApprovedCost.previous,
            )}
          </Typography>
        }
        differenceFooter={
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {formatDashboardDelta(data.kpis.difference.current, data.kpis.difference.previous)}
          </Typography>
        }
      />

      <DashboardAttentionSection
        orders={data.actionRequired.orders}
        standbyOrders={data.actionRequired.standbyOrders}
        uangJalanReports={data.actionRequired.uangJalanReports}
        suratJalan={data.actionRequired.suratJalan}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <DashboardDailyTable series={data.series} timezone={data.period.timezone} />
        <DashboardExpenseMix expensesByType={data.expensesByType} />
      </Box>

      <DashboardStatusBreakdown
        ordersByStatus={data.ordersByStatus}
        reportsByStatus={data.reportsByStatus}
        suratJalanByStatus={data.suratJalanByStatus}
      />

      <DashboardTeamTable
        teams={data.teams}
        selectedTeamOperationsId={teamOperationsId}
        onTeamSelect={handleTeamChange}
      />
    </Box>
  );
};

export default DashboardPage;
